import { useRef } from 'react';
import type { CalendarHoliday, WorkCalendarSummary } from '@nest-recruit/shared';
import { errorMessage } from '../../../../lib/api-client';
import { formatIsoDate } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { isPendingHoliday, useAddHoliday, useRemoveHoliday } from '../../api/calendars-api';

export interface HolidayActions {
  /** Adds at once with a 10-second Undo. `onRefused` gets what was typed back into a form. */
  add(holidayDate: string, name: string, onRefused?: () => void): void;
  /** Removes at once with a 10-second Undo. A holiday still being saved is removed as soon as it is saved (review W20). */
  remove(holiday: CalendarHoliday): void;
}

/**
 * The one way Calendars adds and removes a holiday — a click on the year, the typed row, or the × in
 * the list — so all three behave alike (non-negotiable #11 and §5a rule 8: undo beats confirm).
 *
 * Undo pressed before the server has answered waits for that answer, then reverses it; a refusal
 * closes the Undo, since there is nothing left to undo, and says why.
 */
export function useHolidayActions(calendar: WorkCalendarSummary): HolidayActions {
  const addHoliday = useAddHoliday();
  const removeHoliday = useRemoveHoliday();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const dismiss = useToasts((state) => state.dismiss);
  // Review W20: the adds still waiting for the server, by date, so a click on one is kept rather than ignored.
  const saving = useRef(new Map<string, { readonly done: Promise<CalendarHoliday>; readonly toastId: string | null }>());

  const refused = (message: string, error: unknown): void => {
    show({ tone: 'danger', message, detail: errorMessage(error) });
  };

  function add(holidayDate: string, name: string, onRefused?: () => void, isUndo = false): void {
    const done = addHoliday.mutateAsync({ calendarId: calendar.id, holidayDate, name });
    const toastId = isUndo
      ? null
      : showUndo(`${name} added on ${formatIsoDate(holidayDate)}`, () => {
          done.then((holiday) => remove(holiday, true), () => undefined);
        });
    const entry = { done, toastId };
    saving.current.set(holidayDate, entry);
    const settled = (): void => {
      if (saving.current.get(holidayDate) === entry) saving.current.delete(holidayDate);
    };
    done.then(settled, (error: unknown) => {
      settled();
      if (toastId) dismiss(toastId);
      refused(isUndo ? `${name} wasn't put back` : `${name} wasn't added`, error);
      onRefused?.();
    });
  }

  function remove(holiday: CalendarHoliday, isUndo = false): void {
    if (isPendingHoliday(holiday)) {
      // Clicked again before the server answered: the click is the same as Undo on the add, so that toast goes and the
      // holiday comes off the moment it exists. A refused add needs nothing removed.
      const pending = saving.current.get(holiday.holidayDate);
      if (!pending) return;
      if (pending.toastId) dismiss(pending.toastId);
      pending.done.then((saved) => remove(saved, isUndo), () => undefined);
      return;
    }
    const removing = removeHoliday.mutateAsync({ calendarId: calendar.id, holiday });
    const toastId = isUndo
      ? null
      : showUndo(`${holiday.name} removed`, () => {
          removing.then(() => add(holiday.holidayDate, holiday.name, undefined, true), () => undefined);
        });
    removing.catch((error: unknown) => {
      if (toastId) dismiss(toastId);
      refused(isUndo ? `${holiday.name} wasn't taken off again` : `${holiday.name} wasn't removed`, error);
    });
  }

  return { add: (holidayDate, name, onRefused) => add(holidayDate, name, onRefused), remove: (holiday) => remove(holiday) };
}
